import { EditedNotificationEventItem } from './common/edited'
import { RevertedNotificationEventItem } from './common/reverted'
import { FieldDesignChangeStatus } from '../statuses/fdc'
import { FDCConcurrenceLevelSideEffect } from '../concurrenceLevelSideEffects'
import { CommentedNotificationEventItem } from './common/commented'
import {
  AllApprovalsNotificationEventItem,
  PartialApprovalNotificationEventItem,
} from './common/approval'
import { NotificationEventItem } from './NotificationEventItem'
import { NotificationEventEnum } from '.'

const fdc = 'Field Design Change' as const

const Submitted = new NotificationEventItem({
  id: '5CB02BC1-5E53-48CB-8D45-E24E88F9C9A1',
  name: 'Submitted',
  description: `A ${fdc} is sent from ${FieldDesignChangeStatus.Draft.name} to ${FieldDesignChangeStatus.PendingLevelConcurrences.name}`,
})
const Edited = EditedNotificationEventItem({
  id: '6C811AC2-81B5-4B3E-AF02-5EC634CF7509',
  editable: fdc,
})
const ResolutionEdited = new NotificationEventItem({
  id: '85553AEC-22BD-49AD-B729-965BADF6DAEF',
  name: 'Resolution Edited',
  description: `A ${fdc} Resolution is edited`,
})
const AnyLevelConcurred = PartialApprovalNotificationEventItem({
  id: '5F47C043-7BE9-4046-838B-6704A42BCBA4',
  name: 'Any Level Concurred',
  description: {
    object: fdc,
    group: 'Concurrence Group',
    approves: 'concurs on the Concurrence Level of',
  },
})
const ConcurrenceLevelRejected = new NotificationEventItem({
  id: '051A831F-17C9-4AF2-9814-FA89AD3EBF7A',
  name: 'Concurrence Level Rejected',
  description: `Any User rejects the Concurrence Level on behalf of their Concurrence Group, sending the ${fdc} to ${FieldDesignChangeStatus.Draft.name}`,
})
// There isn't a great way to rename this as past tense
const PendingApprovals = AllApprovalsNotificationEventItem({
  id: '1565404A-1B60-4B1D-AF6C-D41F0B792DDB',
  name: 'Pending Approvals',
  description: {
    group: 'Concurrence Group',
    object: fdc,
    sendsTo: FieldDesignChangeStatus.PendingApprovals.name,
    approves: 'concurs on the Concurrence Level of',
  },
})
const GroupApproved = PartialApprovalNotificationEventItem({
  id: '882FC1CA-D999-4450-BEC9-BB5921F25576',
  name: 'Group Approved',
  description: {
    group: 'Approval Group',
    object: fdc,
  },
})
const Closed = AllApprovalsNotificationEventItem({
  id: '8713C31D-32B4-4FBC-9E42-AD51334AFD52',
  name: 'Closed',
  description: {
    group: 'Approval Group',
    object: fdc,
    sendsTo: FieldDesignChangeStatus.Closed.name,
  },
})
// There is a side effect called Early Closure and it's more important to
// have that association than make this Closed Early
const EarlyClosure = new NotificationEventItem({
  id: '1B09A737-601F-4668-9EB7-60FDE5954465',
  name: 'Early Closure',
  description: `A Concurrence Level with the ${FDCConcurrenceLevelSideEffect.EarlyClosure.name} side effect is submitted. This may result in a newly created Design Submittal`,
})
const Reverted = RevertedNotificationEventItem({
  id: '926175E1-6238-4C81-BD3F-D602D5489D74',
  revertable: fdc,
  from: [
    FieldDesignChangeStatus.Closed,
    FieldDesignChangeStatus.PendingApprovals,
  ],
  to: FieldDesignChangeStatus.PendingLevelConcurrences,
})
const Commented = CommentedNotificationEventItem({
  id: '64D43BDF-BEA3-48A4-8772-A3389AAAB4FD',
  commentable: fdc,
})

export type FDCNotificationEventItem =
  | typeof Submitted
  | typeof Edited
  | typeof ResolutionEdited
  | typeof AnyLevelConcurred
  | typeof ConcurrenceLevelRejected
  | typeof PendingApprovals
  | typeof GroupApproved
  | typeof Closed
  | typeof EarlyClosure
  | typeof Reverted
  | typeof Commented

class FDCNotificationEventEnum extends NotificationEventEnum<
  'FDCNotification',
  FDCNotificationEventItem,
  FDCNotificationEventItem['name'],
  FDCNotificationEventItem['id']
> {
  readonly typename = 'FDCNotification'
  get values() {
    return [
      this.Submitted,
      this.Edited,
      this.ResolutionEdited,
      this.AnyLevelConcurred,
      this.ConcurrenceLevelRejected,
      this.PendingApprovals,
      this.GroupApproved,
      this.Closed,
      this.EarlyClosure,
      this.Reverted,
      this.Commented,
    ]
  }
  readonly Submitted = Submitted
  readonly Edited = Edited
  readonly ResolutionEdited = ResolutionEdited
  readonly AnyLevelConcurred = AnyLevelConcurred
  readonly ConcurrenceLevelRejected = ConcurrenceLevelRejected
  readonly PendingApprovals = PendingApprovals
  readonly GroupApproved = GroupApproved
  readonly Closed = Closed
  readonly EarlyClosure = EarlyClosure
  readonly Reverted = Reverted
  readonly Commented = Commented
}

export const FDCNotificationEvent = new FDCNotificationEventEnum()
